import {
  ActualEntry,
  AppState,
  BalanceAdjustment,
  BudgetItem,
  ExpenseRule,
  IncomeRule,
  OneOffTransaction,
  Plan,
  RepaymentEffect,
} from '../types'
import { ActualsSource, budgetVariance, computeCashflow, expenseAmountOn } from './cashflowEngine'
import { StatementRow, importIds, merchantKey } from './csvImport'
import { addDays, fromISODate, toISODate } from './dateRules'
import { formatDateShort, formatMoney } from './format'
import { makeId } from './id'
import { applyCapitalRepayment, describeRepaymentEffect } from './loanEngine'
import { accountState } from './storage'

/**
 * Deciding what each statement row becomes, and turning those decisions into
 * changes to one account.
 *
 * A statement is the past; the plan is mostly a forecast. So a row is only
 * worth adding when the plan doesn't already account for it: rent the plan
 * already has as a recurring outgoing would otherwise come off twice. Rows are
 * matched against the plan's own projection first, then against budgets,
 * fixed outgoings and income the person has matched that merchant to before,
 * then money in against pay that came in close to what was expected, and only
 * then offered as one-offs.
 */

export type RowAction =
  | { kind: 'oneoff' }
  | { kind: 'skip' }
  /** What a budget occurrence really cost, a variable bill really came to, or income really came in at. */
  | { kind: 'actual'; sourceId: string }
  /** Already in the plan as this item (a recurring bill, pay, a transfer). Nothing is added. */
  | { kind: 'covered'; name: string }
  /**
   * Money out that does something to a loan (an outgoing marked isDebt): see
   * LOAN_ACTIONS. `effect` is what a capital repayment does to the term or
   * payment (default `shorten`); a payoff ignores it.
   */
  | { kind: LoanActionKind; loanId: string; effect?: RepaymentEffect }

/**
 * What a money-out row can do to a loan, each its own group in the "Becomes"
 * dropdown, with option values `<kind>:<loanId>`. SM-40 settles one early;
 * SM-41 adds a capital repayment that keeps it running.
 */
export type LoanActionKind = 'payoff' | 'capital'
export const LOAN_ACTIONS: { kind: LoanActionKind; group: string }[] = [
  { kind: 'payoff', group: 'Pays off a loan early' },
  { kind: 'capital', group: 'Capital repayment on a loan' },
]

/** Outgoings a loan action can be recorded against: active ones marked as debts. */
export function activeLoans(state: AppState): ExpenseRule[] {
  return state.expenses.filter((e) => e.active && e.isDebt)
}

export type RowStatus = 'new' | 'duplicate' | 'before-start'

export interface ReviewRow extends StatementRow {
  importId: string
  merchant: string
  status: RowStatus
  suggested: RowAction
}

/** How far a statement date can be from the plan's date for the same payment: weekends, bank holidays, clearing. */
const MATCH_WINDOW_DAYS = 3
/**
 * How far money in can be from an income's expected amount and still be that
 * income: this share of what's expected, or this many pounds, whichever is
 * larger. Pay a few pence off and Universal Credit paying more both land inside.
 */
const INCOME_MATCH_SHARE = 0.1
const INCOME_MATCH_POUNDS = 5

/** Whether money in of `got` is close enough to an income expected at `expected` to be it. */
export function nearExpectedIncome(got: number, expected: number): boolean {
  return Math.abs(got - expected) <= Math.max(INCOME_MATCH_SHARE * Math.abs(expected), INCOME_MATCH_POUNDS) + 0.005
}

/** The budget, fixed outgoing or income a row can be recorded against, by id. */
export function findSource(state: AppState, id: string): ActualsSource | undefined {
  return state.budgets.find((b) => b.id === id) ?? state.expenses.find((e) => e.id === id) ?? state.incomes.find((i) => i.id === id)
}

/**
 * What the item a row is matched to was expected to come to, beside what the
 * row came to, both positive: for showing the difference before importing.
 * Null for rows not recorded as an actual, or whose item is gone.
 */
export function expectedVsActual(
  state: AppState,
  row: StatementRow,
  action: RowAction,
): { source: ActualsSource; expected: number; got: number } | null {
  if (action.kind !== 'actual') return null
  const source = findSource(state, action.sourceId)
  if (!source) return null
  return { source, expected: expectedAmountFor(source, row.date), got: Math.abs(row.amount) }
}

/**
 * What one occurrence of a budget, fixed outgoing or income around `isoDate`
 * (today by default) is expected to come to, positive. Budgets and income are
 * planned at their own amount every time; a fixed outgoing at the amount its
 * `amountChanges` give for that date (SM-41), as the forecast does.
 */
export function expectedAmountFor(source: ActualsSource, isoDate: string = toISODate(new Date())): number {
  if (source.kind === 'expense') return expenseAmountOn(source, isoDate)
  return Math.abs(source.amount)
}

/** Longest name an option shows before it's cut, so the amount still fits a narrow select. */
export const OPTION_NAME_MAX = 16

/**
 * "Netflix · £10.99": an item's name and expected amount, for the "Becomes"
 * dropdown. A long name is cut (with "…") rather than the amount, since a
 * native option can't be ellipsised from the middle.
 */
export function optionLabel(source: ActualsSource, symbol: string, isoDate?: string): string {
  const name = source.name.length > OPTION_NAME_MAX ? `${source.name.slice(0, OPTION_NAME_MAX - 1)}…` : source.name
  return `${name} · ${formatMoney(expectedAmountFor(source, isoDate), symbol)}`
}

/**
 * "Car loan · £6,000.00": a loan's name and what's still owed, for the loan
 * groups of the dropdown, in optionLabel's style. Just the name when the plan
 * doesn't know the balance.
 */
export function loanOptionLabel(loan: ExpenseRule, symbol: string): string {
  if (loan.principal && loan.principal > 0) return optionLabel({ ...loan, amount: loan.principal }, symbol)
  return loan.name.length > OPTION_NAME_MAX ? `${loan.name.slice(0, OPTION_NAME_MAX - 1)}…` : loan.name
}

const dayDiff = (a: string, b: string) => Math.abs(fromISODate(a).getTime() - fromISODate(b).getTime()) / 86400000

export function reviewRows(state: AppState, rows: StatementRow[]): ReviewRow[] {
  const ids = importIds(rows)
  const imported = new Set<string>()
  for (const o of state.oneOffs) if (o.importId) imported.add(o.importId)
  for (const a of state.actuals) for (const id of a.importIds ?? []) imported.add(id)

  const start = state.settings.startingBalanceDate
  const covered = coveredByPlan(state, rows)
  // Spending is matched to budgets and bills, money in to income.
  const spendByMerchant = new Map<string, ActualsSource>()
  for (const item of [...state.budgets, ...state.expenses]) {
    if (item.active) for (const p of item.matchPatterns ?? []) spendByMerchant.set(p, item)
  }
  const incomeByMerchant = new Map<string, IncomeRule>()
  for (const item of state.incomes) {
    if (item.active) for (const p of item.matchPatterns ?? []) incomeByMerchant.set(p, item)
  }

  const reviewed = rows.map((row, i): ReviewRow => {
    const importId = ids[i]
    const merchant = merchantKey(row.merchantText ?? row.description)
    const status: RowStatus = imported.has(importId) ? 'duplicate' : row.date < start ? 'before-start' : 'new'
    let suggested: RowAction = { kind: 'oneoff' }
    const remembered =
      row.amount < 0 ? spendByMerchant.get(merchant) : row.amount > 0 ? incomeByMerchant.get(merchant) : undefined
    if (status !== 'new') suggested = { kind: 'skip' }
    else if (covered[i]) suggested = { kind: 'covered', name: covered[i]! }
    else if (remembered) suggested = { kind: 'actual', sourceId: remembered.id }
    return { ...row, importId, merchant, status, suggested }
  })

  // Money in still headed for a one-off may be pay that came in a little
  // over or under what was expected.
  const near = nearIncome(
    state,
    reviewed.filter((r) => r.amount > 0 && r.suggested.kind === 'oneoff'),
    rows.filter((_, i) => covered[i]),
  )
  return reviewed.map((r) => {
    const sourceId = near.get(r)
    return sourceId ? { ...r, suggested: { kind: 'actual', sourceId } } : r
  })
}

/**
 * Money-in rows that are an active income's occurrence within a few days, at
 * an amount close to what was expected. Closest date wins, then closest
 * amount; each occurrence is used once, and those the penny-exact matches
 * already took are left out.
 */
function nearIncome(state: AppState, rows: ReviewRow[], exact: StatementRow[]): Map<ReviewRow, string> {
  const found = new Map<ReviewRow, string>()
  if (rows.length === 0) return found
  const dates = rows.map((r) => r.date).sort()
  const from = addDays(fromISODate(dates[0]), -MATCH_WINDOW_DAYS)
  const to = addDays(fromISODate(dates[dates.length - 1]), MATCH_WINDOW_DAYS)
  const expected = state.incomes
    .filter((i) => i.active)
    .flatMap((i) =>
      budgetVariance(state, i, from, to).map((o) => ({ sourceId: i.id, date: o.date, amount: o.budgeted, used: false })),
    )
  for (const row of exact) {
    const taken = expected.find(
      (o) => !o.used && Math.abs(o.amount - row.amount) <= 0.005 && dayDiff(o.date, row.date) <= MATCH_WINDOW_DAYS,
    )
    if (taken) taken.used = true
  }

  // Every possible pairing, closest first, so a nearer row isn't beaten to its
  // occurrence by one that comes earlier in the statement.
  const pairs = rows
    .flatMap((row) =>
      expected
        .filter((o) => !o.used && dayDiff(o.date, row.date) <= MATCH_WINDOW_DAYS && nearExpectedIncome(row.amount, o.amount))
        .map((o) => ({ row, o, days: dayDiff(o.date, row.date), off: Math.abs(o.amount - row.amount) })),
    )
    .sort((a, b) => a.days - b.days || a.off - b.off)
  for (const { row, o } of pairs) {
    if (o.used || found.has(row)) continue
    o.used = true
    found.set(row, o.sourceId)
  }
  return found
}

/**
 * For each row, the name of the plan item it already is, if any: a projected
 * transaction of exactly the same amount within a few days. Each projected
 * one is used once, so two £9.99 subscriptions in a week match two rows, not one.
 * Budgets are left out — they're forecasts of spend, which is what matching to
 * a budget is for.
 */
function coveredByPlan(state: AppState, rows: StatementRow[]): (string | null)[] {
  if (rows.length === 0) return []
  const from = addDays(fromISODate(rows[0].date), -MATCH_WINDOW_DAYS)
  const to = addDays(fromISODate(rows[rows.length - 1].date), MATCH_WINDOW_DAYS)
  const projected = computeCashflow(state, from, to).flatMap((day) =>
    day.transactions
      .filter((t) => t.sourceKind !== 'budget' && t.sourceKind !== 'adjustment')
      .map((t) => ({ date: day.date, amount: t.amount, name: t.name, used: false })),
  )

  return rows.map((row) => {
    let best: (typeof projected)[number] | undefined
    for (const p of projected) {
      if (p.used || Math.abs(p.amount - row.amount) > 0.005) continue
      const d = dayDiff(p.date, row.date)
      if (d > MATCH_WINDOW_DAYS) continue
      if (!best || d < dayDiff(best.date, row.date)) best = p
    }
    if (!best) return null
    best.used = true
    return best.name
  })
}

export interface ImportChanges {
  oneOffs: OneOffTransaction[]
  /** Whole entries, existing ones with the new rows added in. Upsert by (sourceId, date). */
  actuals: ActualEntry[]
  /** Only budgets, outgoings and income whose match patterns changed. */
  budgets: BudgetItem[]
  expenses: ExpenseRule[]
  incomes: IncomeRule[]
  /** Rows matched to an item with no occurrence close enough to belong to, added as one-offs instead. */
  unplaced: number
  /** Budget occurrences the statement covers with nothing spent against them, recorded as £0 actuals. */
  zeroed: number
}

/** How far back (or, failing that, forward) to look for the occurrence a row belongs to. */
const OCCURRENCE_WINDOW_DAYS = 35

export function buildImport(
  state: AppState,
  rows: ReviewRow[],
  actions: Map<string, RowAction>,
  rememberMatches: boolean,
  /** Clean names from the shared catalogue, by merchant key, for the one-offs' names. */
  cleanNames: Record<string, string> = {},
): ImportChanges {
  const oneOffs: OneOffTransaction[] = []
  const actuals = new Map<string, ActualEntry>()
  const learnt = new Map<string, ActualsSource>()
  let unplaced = 0

  const asOneOff = (row: ReviewRow) =>
    oneOffs.push({
      id: makeId(),
      kind: 'oneoff',
      name: cleanNames[row.merchant] ?? row.description,
      amount: row.amount,
      date: row.date,
      importId: row.importId,
    })

  for (const row of rows) {
    if (row.status !== 'new') continue
    const action = actions.get(row.importId) ?? row.suggested
    if (action.kind === 'skip' || action.kind === 'covered') continue
    if (action.kind === 'oneoff') {
      asOneOff(row)
      continue
    }
    if ('loanId' in action) {
      // Recorded against the loan rather than as an actual: an actual stands in
      // for one planned repayment, and a settlement or overpayment isn't one.
      const loan = state.expenses.find((e) => e.id === action.loanId)
      if (!loan || !loan.isDebt || !loan.active || row.amount >= 0) {
        unplaced++
        asOneOff(row)
        continue
      }
      // The same import may already have taught this loan a merchant.
      const current = (learnt.get(loan.id) as ExpenseRule | undefined) ?? loan
      if (action.kind === 'capital') {
        // The lump sum comes off the balance (and the term or payment) on the
        // loan; the one-off is the cash leaving the account, on top of that
        // month's regular repayment, which stays forecast.
        const effect = action.effect ?? 'shorten'
        learnt.set(loan.id, applyCapitalRepayment(current, { date: row.date, amount: row.amount, effect, importId: row.importId }))
        oneOffs.push({
          id: makeId(),
          kind: 'oneoff',
          name: `${loan.name} capital repayment`,
          amount: row.amount,
          date: row.date,
          importId: row.importId,
          loanId: loan.id,
        })
        continue
      }
      // A regular repayment on the statement the same day, left as "Already in
      // plan", is a real payment too: it stays forecast beside the settlement.
      const repaidSameDay = rows.some((r) => {
        if (r.status !== 'new' || r.importId === row.importId || r.date !== row.date) return false
        const a = actions.get(r.importId) ?? r.suggested
        return a.kind === 'covered' && a.name === loan.name
      })
      learnt.set(loan.id, settleLoan(current, row.date, repaidSameDay))
      oneOffs.push({
        id: makeId(),
        kind: 'oneoff',
        name: `${loan.name} settled early`,
        amount: row.amount,
        date: row.date,
        importId: row.importId,
        loanId: loan.id,
      })
      continue
    }

    const item = findSource(state, action.sourceId)
    const occurrence = item && occurrenceFor(state, item, row.date)
    if (!item || !occurrence) {
      unplaced++
      asOneOff(row)
      continue
    }

    // Several shops in one week all belong to that week's occurrence, so they
    // add up. A refund (money in) comes off what was spent. For income it's
    // the other way round: money in is what was received.
    const key = `${item.id}|${occurrence}`
    const existing = actuals.get(key) ?? state.actuals.find((a) => a.sourceId === item.id && a.date === occurrence)
    const entry: ActualEntry = existing
      ? { ...existing, importIds: [...(existing.importIds ?? [])] }
      : { id: makeId(), sourceId: item.id, date: occurrence, amount: 0, importIds: [] }
    const received = item.kind === 'income' ? row.amount : -row.amount
    entry.amount = Math.max(0, Math.round((entry.amount + received) * 100) / 100)
    entry.importIds!.push(row.importId)
    actuals.set(key, entry)

    if (rememberMatches && row.merchant) {
      const current = learnt.get(item.id) ?? item
      if (!(current.matchPatterns ?? []).includes(row.merchant)) {
        learnt.set(item.id, { ...current, matchPatterns: [...(current.matchPatterns ?? []), row.merchant] })
      }
    }
  }

  const zeroed = zeroBudgetActuals(state, rows, actuals)

  const changed = [...learnt.values()]
  return {
    oneOffs,
    actuals: [...actuals.values()],
    budgets: changed.filter((i): i is BudgetItem => i.kind === 'budget'),
    expenses: changed.filter((i): i is ExpenseRule => i.kind === 'expense'),
    incomes: changed.filter((i): i is IncomeRule => i.kind === 'income'),
    unplaced,
    zeroed,
  }
}

/**
 * A budget cycle the statement covers from its first day to its last with
 * nothing spent against it really came to £0, so it gets a £0 actual rather
 * than staying forecast. A cycle the statement ends partway through is left
 * forecast: the rest of its spending may still be to come. Occurrences that
 * already have an actual, by hand or imported, are left alone; spending a later
 * statement matches to one adds onto the £0. Adds the new entries to `actuals`
 * and returns how many there were.
 */
function zeroBudgetActuals(state: AppState, rows: StatementRow[], actuals: Map<string, ActualEntry>): number {
  if (rows.length === 0) return 0
  const dates = rows.map((r) => r.date).sort()
  const first = dates[0] < state.settings.startingBalanceDate ? state.settings.startingBalanceDate : dates[0]
  const last = dates[dates.length - 1]
  if (first > last) return 0
  const dayAfter = toISODate(addDays(fromISODate(last), 1))

  let zeroed = 0
  for (const budget of state.budgets) {
    if (!budget.active) continue
    // Looks past the statement for the occurrence after each one, where its cycle ends.
    const occurrences = budgetVariance(state, budget, fromISODate(first), addDays(fromISODate(last), OCCURRENCE_WINDOW_DAYS))
    for (const [i, o] of occurrences.entries()) {
      const next = occurrences[i + 1]
      if (o.date > last || !next || next.date > dayAfter) break
      const key = `${budget.id}|${o.date}`
      if (o.actual !== undefined || actuals.has(key)) continue
      actuals.set(key, { id: makeId(), sourceId: budget.id, date: o.date, amount: 0, importIds: [] })
      zeroed++
    }
  }
  return zeroed
}

/**
 * The occurrence on or before the date, else the first one after it; null if
 * neither is near. Income goes to its nearest occurrence instead: pay that
 * lands early, before a weekend, belongs to that payday rather than the last.
 */
function occurrenceFor(state: AppState, budget: ActualsSource, date: string): string | null {
  const d = fromISODate(date)
  if (budget.kind === 'income') {
    const around = budgetVariance(state, budget, addDays(d, -OCCURRENCE_WINDOW_DAYS), addDays(d, OCCURRENCE_WINDOW_DAYS))
    let nearest: string | null = null
    for (const o of around) if (nearest === null || dayDiff(o.date, date) < dayDiff(nearest, date)) nearest = o.date
    return nearest
  }
  const before = budgetVariance(state, budget, addDays(d, -OCCURRENCE_WINDOW_DAYS), d)
  if (before.length > 0) return before[before.length - 1].date
  const after = budgetVariance(state, budget, d, addDays(d, OCCURRENCE_WINDOW_DAYS))
  return after[0]?.date ?? null
}

/**
 * A loan settled on `date`: no repayment after that day, and nothing left
 * owing. The engine's endDate is inclusive, so it ends the day before, and a
 * repayment due on the day itself is the settlement rather than a second
 * payment, unless `repaidThatDay` says the statement also shows that day's
 * regular repayment, which then stays forecast (endDate is the day itself).
 * An earlier endDate stays: settling never extends a loan.
 */
export function settleLoan(loan: ExpenseRule, date: string, repaidThatDay = false): ExpenseRule {
  const lastDay = repaidThatDay ? date : toISODate(addDays(fromISODate(date), -1))
  const endDate = loan.endDate && loan.endDate < lastDay ? loan.endDate : lastDay
  return { ...loan, endDate, principal: 0 }
}

/** Off by more than this share of what's owing… */
const PAYOFF_FAR_SHARE = 0.1
/** …or this many pounds, whichever is larger. */
const PAYOFF_FAR_POUNDS = 50

/** Whether a settlement of `paid` is far from the `principal` the plan says is owed. Unknown (0 or unset) never is. */
export function farFromPrincipal(paid: number, principal: number | undefined): boolean {
  if (!principal || principal <= 0) return false
  return Math.abs(Math.abs(paid) - principal) > Math.max(PAYOFF_FAR_SHARE * principal, PAYOFF_FAR_POUNDS) + 0.005
}

export interface PayoffPreview {
  loanName: string
  /** The row's date: the loan's last day, as people say it (endDate itself is the day before). */
  endsOn: string
  /** The loan's last scheduled repayment before this, or its endDate when none falls in range; null with no endDate. */
  wasEnding: string | null
  /** The loan's endDate was already before the settlement, so it stays. */
  alreadyEnded: boolean
  /** "Car loan ends 14 Oct 2026 (was Mar 2028)". */
  text: string
  farOff: boolean
  /** Set when farOff: says the amount may include interest, fees or a part payment. */
  warning: string | null
}

const monthYear = (iso: string) => fromISODate(iso).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' })

/** What settling `loan` with `row` will change, to show before importing. */
export function payoffPreview(state: AppState, loan: ExpenseRule, row: StatementRow, symbol: string): PayoffPreview {
  const settled = settleLoan(loan, row.date)
  const alreadyEnded = loan.endDate !== undefined && settled.endDate === loan.endDate
  let wasEnding: string | null = null
  if (loan.endDate) {
    const from = fromISODate(row.date < loan.endDate ? row.date : loan.endDate)
    const scheduled = budgetVariance(state, loan, addDays(from, -OCCURRENCE_WINDOW_DAYS), fromISODate(loan.endDate))
    wasEnding = scheduled[scheduled.length - 1]?.date ?? loan.endDate
  }
  const text = alreadyEnded
    ? `${loan.name} already ended ${monthYear(wasEnding!)}; recorded as its settlement`
    : `${loan.name} ends ${formatDateShort(fromISODate(row.date))} (${wasEnding ? `was ${monthYear(wasEnding)}` : 'no end date before'})`
  const far = farFromPrincipal(row.amount, loan.principal)
  return {
    loanName: loan.name,
    endsOn: row.date,
    wasEnding,
    alreadyEnded,
    text,
    farOff: far,
    warning: far
      ? `${formatMoney(Math.abs(row.amount), symbol)} is far from the ${formatMoney(loan.principal!, symbol)} the plan says is owed. It may include interest, fees or a part payment; it can still be imported.`
      : null,
  }
}

/**
 * What recording `row` as a capital repayment on `loan` will do, to show
 * before importing: "Mortgage: balance £182,400.00 → £172,400.00; ends Jun
 * 2049 (was Mar 2051)" or "…; payment £1,013.90 → £958.30 from Jul 2027".
 */
export function capitalPreview(loan: ExpenseRule, row: StatementRow, effect: RepaymentEffect, symbol: string): string {
  const after = applyCapitalRepayment(loan, { date: row.date, amount: row.amount, effect })
  return `${loan.name}: ${describeRepaymentEffect(loan, after, row.date, symbol)}`
}

/** The account as it would be with the import applied — for working out the reconcile. */
export function withImport(state: AppState, changes: ImportChanges): AppState {
  const replaced = new Set(changes.actuals.map((a) => `${a.sourceId}|${a.date}`))
  return {
    ...state,
    oneOffs: [...state.oneOffs, ...changes.oneOffs],
    actuals: [...state.actuals.filter((a) => !replaced.has(`${a.sourceId}|${a.date}`)), ...changes.actuals],
    budgets: state.budgets.map((b) => changes.budgets.find((c) => c.id === b.id) ?? b),
    expenses: state.expenses.map((e) => changes.expenses.find((c) => c.id === e.id) ?? e),
    incomes: state.incomes.map((i) => changes.incomes.find((c) => c.id === i.id) ?? i),
  }
}

export interface Reconcile {
  date: string
  statementBalance: number
  /** The plan's balance on `date`, less `stillToSpend`. */
  projected: number
  /** Forecasts of budget cycles still open on `date`, which the plan takes off on day one but the bank hasn't seen yet. */
  stillToSpend: number
  /** statementBalance − projected: what an adjustment on `date` would add. */
  delta: number
}

/**
 * Where the statement's last balance and the plan disagree once the import is
 * in. Rows are oldest-first, so the last one with a balance is the latest
 * closing figure. Null when they agree, the statement has no balances, or the
 * date is before the plan starts.
 *
 * A budget cycle the statement ends partway through is left out: the plan takes
 * its whole forecast off on its first day, but the bank only has what's been
 * spent so far. Counting it would make the adjustment cover spending still to
 * come, and the adjustment stays when a later statement records that spending.
 */
export function reconcileWith(stateAfter: AppState, rows: StatementRow[]): Reconcile | null {
  const last = [...rows].reverse().find((r) => r.balance !== undefined && r.date >= stateAfter.settings.startingBalanceDate)
  if (!last || last.balance === undefined) return null
  const day = computeCashflow(stateAfter, fromISODate(last.date), fromISODate(last.date))[0]
  if (!day) return null
  const stillToSpend = openBudgetForecast(stateAfter, last.date)
  const projected = Math.round((day.balance + stillToSpend) * 100) / 100
  const delta = Math.round((last.balance - projected) * 100) / 100
  if (Math.abs(delta) < 0.01) return null
  return { date: last.date, statementBalance: last.balance, projected, stillToSpend, delta }
}

/**
 * What the plan takes off for budget cycles still open at the end of `date`
 * with nothing recorded against them yet. One with an actual is already at
 * what was spent so far, so it adds nothing.
 */
function openBudgetForecast(state: AppState, date: string): number {
  const d = fromISODate(date)
  const dayAfter = toISODate(addDays(d, 1))
  let total = 0
  for (const budget of state.budgets) {
    if (!budget.active) continue
    const around = budgetVariance(state, budget, addDays(d, -OCCURRENCE_WINDOW_DAYS), addDays(d, OCCURRENCE_WINDOW_DAYS))
    let at = -1
    while (at + 1 < around.length && around[at + 1].date <= date) at++
    const current = around[at]
    if (!current || current.date < state.settings.startingBalanceDate || current.actual !== undefined) continue
    const next = around[at + 1]
    if (!next || next.date > dayAfter) total += current.budgeted
  }
  return Math.round(total * 100) / 100
}

export function reconcileAdjustment(r: Reconcile): BalanceAdjustment {
  return { id: makeId(), kind: 'adjustment', date: r.date, delta: r.delta, note: 'Matched to bank statement', active: true }
}

/**
 * Whether a statement looks like it belongs to another account (SM-25). Its
 * balance before the first row should be close to what the plan expects the
 * account to hold that morning. When it's well off, the reconcile adjustment
 * would only cover the gap up, so the person is told instead.
 */
export interface OpeningCheck {
  /** The statement's first day. */
  date: string
  /** What the statement says the account held at the start of that day. */
  statementOpening: number
  /** What the chosen account's plan expects then. */
  expected: number
  /** Another account whose plan fits the statement clearly better. */
  better?: { accountId: string; name: string }
}

/** Off by more than this many pounds… */
const FAR_OFF_AMOUNT = 50
/** …and by more than this share of the larger of the two balances. */
const FAR_OFF_SHARE = 0.1

export function farOff(statementOpening: number, expected: number): boolean {
  const gap = Math.abs(statementOpening - expected)
  return gap > FAR_OFF_AMOUNT && gap > FAR_OFF_SHARE * Math.max(Math.abs(statementOpening), Math.abs(expected))
}

/**
 * The balance before the statement's first row: its first printed balance,
 * less every row up to and including that one. Null without balances.
 */
export function statementOpening(rows: StatementRow[]): { date: string; balance: number } | null {
  const at = rows.findIndex((r) => r.balance !== undefined)
  if (at === -1) return null
  const moved = rows.slice(0, at + 1).reduce((sum, r) => sum + Math.round(r.amount * 100), 0)
  return { date: rows[0].date, balance: (Math.round(rows[at].balance! * 100) - moved) / 100 }
}

/**
 * What the plan expects the account to hold at the start of a day: the
 * starting balance on its starting date, the day before's close after it.
 * Null before the starting balance date, which the plan knows nothing about.
 */
export function expectedOpening(state: AppState, date: string): number | null {
  const start = state.settings.startingBalanceDate
  if (date < start) return null
  if (date === start) return state.settings.startingBalance
  const dayBefore = addDays(fromISODate(date), -1)
  return computeCashflow(state, dayBefore, dayBefore)[0]?.balance ?? null
}

/**
 * Null when the statement fits the chosen account, or there's no telling (no
 * balances, or it starts before the account's starting balance date).
 */
export function checkOpening(plan: Plan, accountId: string, rows: StatementRow[]): OpeningCheck | null {
  const opening = statementOpening(rows)
  const chosen = plan.accounts.find((a) => a.id === accountId)
  if (!opening || !chosen) return null
  const expected = expectedOpening(accountState(plan, chosen), opening.date)
  if (expected === null || !farOff(opening.balance, expected)) return null

  // Clearly better means fitting where the chosen one doesn't, and being the only one that does.
  const fits = plan.accounts.filter((a) => {
    if (a.id === accountId) return false
    const theirs = expectedOpening(accountState(plan, a), opening.date)
    return theirs !== null && !farOff(opening.balance, theirs)
  })
  return {
    date: opening.date,
    statementOpening: opening.balance,
    expected,
    ...(fits.length === 1 ? { better: { accountId: fits[0].id, name: fits[0].name } } : {}),
  }
}
